/**
 * Pure logic behind the post-sign-in workspace picker (tenant-scoped sign-in, 2026-10-10): the
 * order the choices are offered in, and the "last used" wording. Kept free of React so the
 * resolution rules are unit-tested on their own (Rule 33).
 */

export interface WorkspacePickerOption {
  readonly id: string;
  readonly name: string;
  /** The person's role in that workspace — owner, admin, editor… */
  readonly role: string;
  /** When this device last chose it (ISO), or null if never. */
  readonly lastUsedAt: string | null;
}

/**
 * The order to offer workspaces in: most recently used on THIS device first; never-used ones after,
 * keeping the server's order (owner-first, oldest-first) among themselves.
 */
export function orderWorkspaceOptions(options: readonly WorkspacePickerOption[]): WorkspacePickerOption[] {
  return options
    .map((o, index) => ({ o, index, t: o.lastUsedAt ? Date.parse(o.lastUsedAt) : Number.NaN }))
    .sort((a, b) => {
      const au = Number.isFinite(a.t);
      const bu = Number.isFinite(b.t);
      if (au && bu) return b.t - a.t;
      if (au !== bu) return au ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ o }) => o);
}

/** "Last used today", "… yesterday", "… 3 days ago", "… on 4 Sep 2026", or "Not used on this device". */
export function lastUsedLabel(lastUsedAt: string | null, now: Date = new Date()): string {
  if (!lastUsedAt) return 'Not used on this device';
  const at = new Date(lastUsedAt);
  if (Number.isNaN(at.getTime())) return 'Not used on this device';
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(at)) / 86_400_000);
  if (days <= 0) return 'Last used today';
  if (days === 1) return 'Last used yesterday';
  if (days < 7) return `Last used ${days} days ago`;
  return `Last used on ${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/**
 * Should the picker be shown before entering the app? Only for a person with more than one usable
 * workspace who has not chosen yet in this browser session, and never while a named tenant (a
 * tenant link or ID) is being entered — that choice was already made.
 */
export function shouldShowWorkspacePicker(input: {
  readonly workspaceCount: number;
  readonly chosenThisSession: boolean;
  readonly enteringNamedTenant: boolean;
}): boolean {
  return input.workspaceCount > 1 && !input.chosenThisSession && !input.enteringNamedTenant;
}

// ── Per-device memory ────────────────────────────────────────────────────────
// "Last used" is per DEVICE (localStorage); "already chosen" is per browser SESSION
// (sessionStorage), so a new sign-in session offers the picker again with the last choice on top.
// Storage can throw (private mode, blocked site data): every access is guarded, and a failure only
// means nothing is remembered.

const LAST_USED_KEY = 'inferrex.workspaceLastUsed';
const CHOSEN_KEY = 'inferrex.workspaceChosen';

interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function storage(kind: 'localStorage' | 'sessionStorage'): KeyValueStore | undefined {
  try {
    return (globalThis as Record<string, unknown>)[kind] as KeyValueStore | undefined;
  } catch {
    return undefined;
  }
}

/** Workspace id → when this device last chose it (ISO). */
export function readWorkspaceLastUsed(): Record<string, string> {
  try {
    const raw = storage('localStorage')?.getItem(LAST_USED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string'));
  } catch {
    return {};
  }
}

/** Remember that this device chose `workspaceId` now, and that this session has chosen. */
export function recordWorkspaceChoice(workspaceId: string, now: Date = new Date()): void {
  try {
    const next = { ...readWorkspaceLastUsed(), [workspaceId]: now.toISOString() };
    storage('localStorage')?.setItem(LAST_USED_KEY, JSON.stringify(next));
  } catch { /* not remembered */ }
  try { storage('sessionStorage')?.setItem(CHOSEN_KEY, '1'); } catch { /* not remembered */ }
}

/** Has this browser session already chosen a workspace? */
export function workspaceChosenThisSession(): boolean {
  try {
    return storage('sessionStorage')?.getItem(CHOSEN_KEY) === '1';
  } catch {
    return false;
  }
}
